import React from 'react';
import Icon from '../../../components/AppIcon';
import { getEventTypeStyle, getEstadoStyle } from './AdvancedFilters';
import { useAuth } from '../../../contexts/AuthContext';

const EventDetailModalNew = ({ isOpen, evento, empleados, onClose, onEdit, onDelete }) => {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.rol === 'admin';

  if (!isOpen || !evento) return null;

  const empleado = empleados?.find(e => e?.id === evento?.empleadoId);
  const typeStyle = getEventTypeStyle(evento?.tipoServicio);
  const estadoStyle = getEstadoStyle(evento?.estado);

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha + 'T00:00:00')?.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`Hola, te contactamos desde Las Dos Doncellas sobre el evento del ${evento?.fecha}.`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const ESTADOS_LIST = ['Pendiente', 'Confirmado', 'En preparación', 'En proceso', 'Finalizado', 'Cancelado', 'PROGRAMADO', 'REALIZADO', 'CANCELADO'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#1A1A1A] rounded-2xl border border-white/10 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4"
        onClick={e => e?.stopPropagation()}>
        
        {/* Header */}
        <div className="sticky top-0 bg-[#1A1A1A] border-b border-white/8 px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div>
            <h2 className="text-xl font-oranienbaum font-bold text-white">{evento?.cliente || 'Sin cliente'}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-xs font-oranienbaum px-2.5 py-1 rounded-full border" style={{ color: typeStyle?.color, borderColor: `${typeStyle?.color}40`, background: typeStyle?.bg }}>
                {evento?.tipoServicio || 'Sin tipo'}
              </span>
              <span className="text-xs font-oranienbaum px-2.5 py-1 rounded-full border" style={{ color: estadoStyle?.color, borderColor: `${estadoStyle?.color}40`, background: estadoStyle?.bg }}>
                {evento?.estado || 'Sin estado'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} style={{ color: '#ffffff60' }} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Date/Time/Location */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/3 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Fecha</p>
              <p className="text-sm font-oranienbaum text-white capitalize">{formatDate(evento?.fecha)}</p>
            </div>
            <div className="bg-white/3 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Hora</p>
              <p className="text-sm font-oranienbaum text-white">{evento?.horaInicio || '--:--'}{evento?.horaFin ? ` – ${evento?.horaFin}` : ''}</p>
            </div>
          </div>

          {evento?.ubicacion && (
            <div className="bg-white/3 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Dirección / Provincia</p>
              <p className="text-sm font-oranienbaum text-white flex items-center gap-2">
                <Icon name="MapPin" size={13} style={{ color: '#C9A24D' }} />
                {evento?.ubicacion}
              </p>
            </div>
          )}

          {/* Employee */}
          {empleado && (
            <div className="bg-white/3 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Empleado Asignado</p>
              <p className="text-sm font-oranienbaum text-white flex items-center gap-2">
                <Icon name="User" size={13} style={{ color: '#3A7F6B' }} />
                {empleado?.nombre}
              </p>
            </div>
          )}

          {/* Piezas / Kg */}
          {(evento?.numPiezas || evento?.descripcionPiezas) && (
            <div className="grid grid-cols-2 gap-4">
              {evento?.numPiezas && (
                <div className="bg-white/3 rounded-xl p-3 border border-white/5">
                  <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Kg Previstos</p>
                  <p className="text-sm font-oranienbaum font-semibold text-white">{evento?.numPiezas} kg</p>
                </div>
              )}
              {evento?.descripcionPiezas && (
                <div className="bg-white/3 rounded-xl p-3 border border-white/5">
                  <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-1">Descripción</p>
                  <p className="text-sm font-oranienbaum text-white/80">{evento?.descripcionPiezas}</p>
                </div>
              )}
            </div>
          )}

          {/* Financial (admin only) */}
          {isAdmin && (
            <div className="bg-[#D6EF4E]/5 rounded-xl p-4 border border-[#D6EF4E]/15">
              <p className="text-[10px] font-oranienbaum text-[#D6EF4E]/60 uppercase tracking-wide mb-3">Información Financiera</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] text-white/40 font-oranienbaum mb-0.5">Importe Previsto</p>
                  <p className="text-lg font-oranienbaum font-bold" style={{ color: '#D6EF4E' }}>
                    {evento?.precioServicio ? `${parseFloat(evento?.precioServicio)?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-white/40 font-oranienbaum mb-0.5">Gastos</p>
                  <p className="text-lg font-oranienbaum font-bold text-red-400">
                    {evento?.gastos ? `${parseFloat(evento?.gastos)?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €` : '-'}
                  </p>
                </div>
                {evento?.precioServicio && evento?.gastos && (
                  <div>
                    <p className="text-[10px] text-white/40 font-oranienbaum mb-0.5">Beneficio</p>
                    <p className="text-lg font-oranienbaum font-bold text-[#3A7F6B]">
                      {(parseFloat(evento?.precioServicio) - parseFloat(evento?.gastos))?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Observaciones */}
          {evento?.observaciones && (
            <div className="bg-white/3 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-2">Observaciones</p>
              <p className="text-sm font-oranienbaum text-white/70 whitespace-pre-wrap">{evento?.observaciones}</p>
            </div>
          )}

          {/* Estado badges */}
          <div>
            <p className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide mb-2">Estado del Evento</p>
            <div className="flex flex-wrap gap-2">
              {ESTADOS_LIST?.map(est => {
                const s = getEstadoStyle(est);
                const isActive = evento?.estado === est;
                return (
                  <span key={est} className={`text-xs font-oranienbaum px-2.5 py-1 rounded-full border transition-all ${isActive ? 'opacity-100' : 'opacity-30'}`}
                    style={{ color: s?.color, borderColor: `${s?.color}40`, background: isActive ? s?.bg : 'transparent' }}>
                    {est}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-[#1A1A1A] border-t border-white/8 px-6 py-4 rounded-b-2xl">
          <div className="flex flex-wrap gap-2">
            <button onClick={handleWhatsApp}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/20 transition-all">
              <Icon name="MessageCircle" size={15} />WhatsApp
            </button>
            <button onClick={() => { onEdit(evento); onClose(); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-[#3A7F6B]/10 text-[#3A7F6B] border border-[#3A7F6B]/30 hover:bg-[#3A7F6B]/20 transition-all">
              <Icon name="Edit" size={15} />Editar
            </button>
            <button onClick={() => { onDelete(evento?.id); onClose(); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all">
              <Icon name="Trash2" size={15} />Eliminar
            </button>
            <button onClick={onClose}
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 transition-all">
              <Icon name="X" size={15} />Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailModalNew;
